import { FC, ReactNode, useCallback, useState } from "react"
import ModalDark from "../modal-dark/ModalDark"
import ModalDarkContent from "../modal-dark/ModalDarkContent"
import {
  ModalDarkContentValue,
  ModalDarkContext,
  ModalDarkMode,
  OpenDarkModalParams,
  TopOffsetValues,
  WidthKey,
  defaultModalDarkValues,
} from "./ModalDarkContext"

type Props = {
  children: ReactNode
}

const ModalDarkProvider: FC<Props> = ({ children }) => {
  const [isModalDarkOpen, setIsModalDarkOpen] = useState(defaultModalDarkValues.isModalDarkOpen)
  const [modalDarkTypes, setModalDarkTypes] = useState(defaultModalDarkValues.modalDarkTypes)
  const [widthDarkModal, setWidthDarkModal] = useState<WidthKey>(defaultModalDarkValues.widthDarkModal)
  const [modalDarkMode, setModalDarkMode] = useState<ModalDarkMode>(defaultModalDarkValues.modalDarkMode)
  const [isLogo, setIsLogo] = useState(defaultModalDarkValues.isLogo)
  const [modalDarkContent, setModalDarkContent] = useState<ModalDarkContentValue | null | undefined>(
    defaultModalDarkValues.modalDarkContent
  )
  const [contentReviewCard, setContentReviewCard] = useState<ComponentUiReview | undefined>(
    defaultModalDarkValues.contentReviewCard
  )
  const [onSubmit, setOnSubmit] = useState<(() => void) | undefined>()
  const [topOffset, setTopOffset] = useState<number | undefined>(defaultModalDarkValues.topOffset)
  const [centered, setCentered] = useState(defaultModalDarkValues.centered)

  // Memoized: only state setters are used inside, so the reference stays stable and
  // consumers can safely keep it in their effect dependencies
  const openDarkModal = useCallback(
    <T extends ModalDarkContentValue>({
      type,
      content,
      contentReviewCard,
      isLogo,
      mode,
      width,
      topOffset: topOffsetPreset,
      centered: centeredParam,
    }: OpenDarkModalParams<T>) => {
      setModalDarkTypes(type)
      if (content !== undefined) setModalDarkContent(content)
      if (contentReviewCard) setContentReviewCard(contentReviewCard)
      if (width) setWidthDarkModal(width)
      setIsModalDarkOpen(true)
      setModalDarkMode(mode ?? "default")
      setIsLogo(isLogo ?? false)
      // Handle topOffset preset → pixel value; "centered" preset maps to centered=true + no offset
      if (topOffsetPreset) {
        setCentered(topOffsetPreset === "centered")
        setTopOffset(TopOffsetValues[topOffsetPreset])
      } else if (centeredParam !== undefined) {
        setCentered(centeredParam)
        setTopOffset(undefined)
      } else {
        setCentered(false)
        setTopOffset(undefined)
      }
    },
    []
  )

  return (
    <ModalDarkContext.Provider
      value={{
        modalDarkTypes,
        setModalDarkTypes,
        isModalDarkOpen,
        setIsModalDarkOpen,
        widthDarkModal,
        setWidthDarkModal,
        openDarkModal,
        modalDarkMode,
        setModalDarkMode,
        isLogo,
        setIsLogo,
        modalDarkContent,
        setModalDarkContent,
        contentReviewCard,
        onSubmit,
        setOnSubmit,
        topOffset,
        centered,
      }}
    >
      {children}
      <ModalDark open={isModalDarkOpen}>{modalDarkTypes && <ModalDarkContent />}</ModalDark>
    </ModalDarkContext.Provider>
  )
}

export default ModalDarkProvider
